// src/Pages/Dashboard/Dashboard.jsx
// ------------------------------------------------------------
// Suojattu Dashboard sivu esiintyjänhallintaan
// ------------------------------------------------------------

import { useEffect, useState, useRef, useCallback, Fragment } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs.jsx";
import { PageOverlayLoading } from "../../components/Loading/Spinner.jsx";
import SafeImg from "../../components/SafeImg/SafeImg.jsx";
import { FaStar, FaRegStar } from "react-icons/fa";  // ★ rating badgeen
import heroImg from "../../assets/backstage_dark.webp";

import AdCard from "../../components/Mainoskortti/Mainoskortti.jsx";
import { Trash2 } from "lucide-react";
import WebDevPromoModal from "../../components/WebDevPromoModal/WebDevPromoModal.jsx";

// TABS
import BasicTab from "components/DashboardTabs/BasicTab";
import SocialTab from "../../components/DashboardTabs/SocialTab.jsx";
import MediaTab from "../../components/DashboardTabs/MediaTab.jsx";
import CalendarTab from "../../components/DashboardTabs/CalendarTab.jsx";
import PricingTab from "../../components/DashboardTabs/PricingTab.jsx";
import PublishChecklist from "../../components/PublishChecklist/PublishChecklist.jsx";
import { absolutize } from "../../utils/media.js";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog.jsx";
import bandPlaceholderLocal from "../../assets/delva-media/placeholders/bandplaceholder.webp"; // tai mikä teillä on repossa

// ---------------- Konfiguraatio ----------------

// Sallitut bändi-tabit (käytetään sekä initissä että URL-parsinnassa)
const ALLOWED_TABS = new Set(["basic", "social", "media", "calendar", "pricing", "sets", "uprofile"]);

 // Toast-avaimet
 const PREVIEW_TOAST_KEY = "toast:return-from-preview";

// ⬇️ Esikatselusta palaamisen muistipaikat + per-bändi viimeisin tab
const PREVIEW_RETURN_BAND_KEY = "dashboard.return.band";
const PREVIEW_RETURN_TAB_KEY  = "dashboard.return.tab";
const LAST_TAB_PREFIX         = "dashboard.lastTab."; // esim. dashboard.lastTab.219 = "pricing"

// --- Pieni, kevyt hero-kuvakomponentti (korvaa videon) ---
function HeroImageBanner({ src, alt, className = "" }) {
  return (
    <div className={`relative ${className}`}>
      <SafeImg
        src={src}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover rounded-2xl"
      />
      {/* ohut kehys */}
      <div className="absolute inset-0 rounded-2xl ring-1 ring-black/10 dark:ring-white/10" />
      {/* kevyt gradientti päälle */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-black/15 via-transparent to-black/25 pointer-events-none" />
    </div>
  );
}

function PasswordDialog({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    if (!next || next.length < 8) {
      toast.info("Uusi salasana vähintään 8 merkkiä.", { containerId: "global" });
      return;
    }
    setBusy(true);
    try {
      await api.auth.changePassword({ current_password: cur, new_password: next });
      toast.success("Salasana vaihdettu.", { containerId: "global" });
      onClose?.();
      setCur(""); setNext("");
    } catch (e) {
      const code = e?.payload?.error;
      toast.error(code === "wrong_password" ? "Nykyinen salasana on väärin." : "Vaihto epäonnistui.", { containerId: "global" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" role="dialog" aria-modal="true">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white dark:bg-black/80 ring-1 ring-black/10 dark:ring-white/10 p-4">
        <h3 className="font-semibold mb-3">Vaihda salasana</h3>
        <label className="block text-xs mb-1">Nykyinen salasana</label>
        <input type="password" value={cur} onChange={(e)=>setCur(e.target.value)}
               className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10" autoFocus />
        <label className="block text-xs mt-3 mb-1">Uusi salasana</label>
        <input type="password" value={next} onChange={(e)=>setNext(e.target.value)}
               className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10" />
        <div className="mt-4 flex gap-2 justify-end">
          <NeonButton type="button" onClick={onClose} disabled={busy}>Peruuta</NeonButton>
          <NeonButton type="submit" variant="primary" disabled={busy}>{busy ? "Vaihdetaan…" : "Vaihda"}</NeonButton>
        </div>
      </form>
    </div>
  );
}


  function DashMiniCard({
    title,
    subtitle,
    image,
    badge,
    onClick,
    active = false,
    imageFit = "cover",
  }) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={[
          "group relative overflow-hidden rounded-2xl border border-white/10 dark:border-white/5",
          "bg-white/60 dark:bg-black/50 backdrop-blur shadow-lg",
          "transition-transform duration-300 hover:shadow-neon hover:scale-[1.03]",
          "w-full text-left h-full flex flex-col",
          active ? "outline outline-2 outline-pink/70" : ""
        ].join(" ")}
      >
        {/* sheen + neon ring */}
        <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-black/5 dark:ring-white/10 transition-shadow duration-500 group-hover:shadow-[0_0_24px_rgba(49,255,217,0.35),0_0_48px_rgba(122,0,255,0.25)]" />
        <span className="pointer-events-none absolute -left-1/3 top-0 h-full w-2/3 bg-gradient-to-r from-transparent via-white/15 to-transparent translate-x-[-120%] group-hover:translate-x-[180%] transition-transform duration-[1200ms] ease-out rounded-2xl" />

        {/* Media – sama korkeus kuin AdCardissa */}
        <div className="relative block w-full h-40 rounded-t-2xl overflow-hidden">
          {image ? (
            <SafeImg
              src={image}
              alt=""
              className={[
                "absolute inset-0 w-full h-full",
                imageFit === "contain" ? "object-contain" : "object-cover",
                "bg-black/10"
              ].join(" ")}
            />
          ) : (
            <div className="absolute inset-0" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20" />

          {badge && (
            <div className="absolute top-2 left-2 z-10 px-2 py-1 rounded-full text-white text-xs font-semibold tracking-wide bg-gradient-to-r from-electric to-primary border border-white/20 shadow-neon-primary">
              {badge}
            </div>
          )}
        </div>

        {/* Tekstit – samat marginit ja värit kuin AdCardissa */}
        <div className="p-3 text-center flex-1 flex flex-col">
          <h3 className="font-customFont2 text-sm md:text-base text-black/85 dark:text-white/90">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-1 text-[11px] md:text-xs text-black/70 dark:text-white/70">
              {renderWithBRs(subtitle)}
            </p>
          )}

          {/* Spacer, joka vastaa AdCardin napin aluetta (mt-3 + h-9/10) */}
          <div className="mt-3 h-9 md:h-10" aria-hidden />
        </div>
      </button>
    );
  }

const renderWithBRs = (s) =>
  String(s)
    .split(/<br\s*\/?>/i)
    .map((chunk, i, arr) => (
      <Fragment key={i}>
        {chunk}
        {i < arr.length - 1 && <br />}
      </Fragment>
    ));

// Kalenterin pitoajan (TTL) tunnit:
// 1) REACT_APP_HOLD_TTL_HOURS (uusi)
// 2) REACT_APP_REVIEW_TTL_HOURS (taaksepäinyhteensopivuus)
// 3) oletus 1 h
const HOLD_TTL_HOURS = Number(
  process.env.REACT_APP_HOLD_TTL_HOURS ??
  process.env.REACT_APP_REVIEW_TTL_HOURS ?? 1
);

const ttlLabel = HOLD_TTL_HOURS === 1 ? "tunti" : `${HOLD_TTL_HOURS} tuntia`;

// Web-dev -mainosmodaalin pääkytkin
// false = täysin pois käytöstä; true = käytössä storage-sääntöjen mukaan
const ENABLE_WEBDEV_PROMO = false;

// --- DASHBOARD HERO (käyttäjänäkymä) ---
// Voit vaihtaa polun public-kansioon ("/images/…") tai importata tiedoston (katso alla).
const DASH_HERO = {
  image: heroImg,     // ✅ public-polku toimii suoraan
  hMobile: "h-52",
  // poista pilkut ja käytä välilyöntejä:
  hDesktop: "md:h-72 lg:h-96 xl:h-96 2xl:h-96",
  focal: [50, 25],
  base: 1.12,
  pan: { x: 1.0, y: 0.6 },
  duration: 26,
  anchor: "top",
};

// Päivitetty: kaikki tekstit (sis. ostetut paikat + nimi/slug -huomio)
const TAB_COPY = {
  user: (user, limits, bands) => {
    // max slottien määrä: ensisijaisesti limits → fallback useriin → 1
    const max = Number.isFinite(+limits?.max_bands)
      ? +limits.max_bands
      : Number.isFinite(+(user?.account?.max_bands ?? user?.band_slots))
      ? Number(user?.account?.max_bands ?? user?.band_slots)
      : 1;

    // käytössä olevat (omistettuina): ensisijaisesti limits.bands_count → fallback listasta
    const used = Number.isFinite(+limits?.bands_count)
      ? +limits.bands_count
      : Array.isArray(bands)
      ? bands.filter(b => String(b.role || '').toLowerCase() === 'owner').length
      : 0;

    const canAdd = !!limits?.can_add_band;

    return {
      title: "Käyttäjätili ja esiintyjähallinta",
      subtitle: (
        <>
          Täällä hallinnoit tiliäsi ja korttien (esiintyjien) käyttöoikeuksia. Aloita kortin hallinta klikkaamalla luo esiintyjäkortti. 
          <br /><br />
          <strong>Korttipaikkasi </strong>: Tililläsi on tällä hetkellä{" "}
          <strong>{max}</strong>{" "}
          <u>korttipaikkaa</u>, joista käytössä <strong>{used}</strong>. 
          <br /><br />
          Hintaan{" "}
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-600/10 text-green-700 dark:text-green-400 border border-green-600/20 font-semibold">
            3,99&nbsp;€/kk
          </span>
           &nbsp;Voit laajentaa uuden esiintyjän hallintaasi. Tällähetkellä
          {" "} {canAdd ? "voit lisätä uuden kortin." : "paikkasi ovat täynnä."}{" "}
          <br /><br />
          {" "}
          <a href="/otayhteytta" className="underline">Ota yhteyttä ylläpitoon</a>, annamme ohjeet korttipaikkojen hankintaan.
        </>
      ),
      tips: [
        "Voit poistaa esiintyjäkortin roskakorikuvakkeesta, jos sinulla ei ole yhtään esiintymistä tai aktiivista varausta.",
        "Pidä yhteystietosi ajan tasalla – niitä käytetään sähköposti-ilmoituksiin ja tuen yhteydenottoihin."
      ],
    };
  },

basic: (user) => ({
  title: "PERUSTIEDOT",
  subtitle: `Hei ${user.display_name || user.email}! Tässä muokkaat esiintyjäprofiilisi perustietoja.
  Kaikki kentät näkyvät julkisesti, paitsi sähköpostiosoite ja puhelinnumero.

  <br /><br />
  Julkaisun tarkistus" -kortti kertoo alhaalla, jos jotain puuttuu profiilistasi. Se päivittyy automaattisesti, muista tallentaa kun teet muutoksia.

  <br /><br />
  Julkaisu: kun vaaditut kentät ovat kunnossa, paina "Lähetä tarkistukseen". Profiili siirtyy tarkistettavaksi ja näkyy haussa hyväksynnän jälkeen. Voit piilottaa profiilin milloin tahansa samasta painikkeesta.
`,
}),

 // Media-osion tekstit (yhtenäinen, ilman toistoa)
media: () => ({
  title: "MEDIA",
  subtitle:
    "Lisää ja hallitse gallerian kuvia, videoita ja ääninäytteitä. Kuvat rajataan automaattisesti neliöön (1:1).",

  tips: [
    "Kuvat: suositus vähintään 1080×1080 px.",
    "Tuetut muodot: WEBP/PNG/JPG. Yhtenäinen kuvasuhde tekee profiilista siistin.",
    "Vinkki: sijoita tärkein sisältö keskelle – reunoja voidaan rajata automaattisesti.",
    "Optimoi: pidä tiedostot kevyinä ja vältä rakeisuutta sekä suurta tekstipeittoa.",
    "Videot: liitä YouTube-linkki (julkinen tai piilotettu) ja varmista, että upotus on sallittu.",
    "Ääni: liitä SoundCloud-linkki (yksittäinen raita tai soittolista).",
  ],
}),


calendar: () => ({
  title: "ESIINTYMISKALENTERI",
  subtitle:
    "Avaa vihreäksi ne päivät, joille otat tarjouspyyntöjä. Asiakas näkee vain avoimet päivät. Kun asiakas hyväksyy määrittämäsi hinnan, päivä muuttuu varatuksi (myyty) ja saat ilmoituksen sähköpostiin.",

  tips: [
    "Klikkaa päivää: kiinni ↔ buukattavissa (vihreä).",
    `Tarjous vireillä (keltainen): asiakkaalla ${ttlLabel} aikaa hyväksyä tai hylätä.`,
    "Varattu (punainen): keikka myyty.",
    "Ei buukattavissa (harmaa)"
  ],
})

,
pricing: () => ({
  title: "HINNOITTELU",
  subtitle:
    "Täällä määrität esiintymisalueesi, tarjoamiesi settien pituudet ja niiden hinnat. Hintoihin lisätään 0,59 €/km matkakuluja antamastasi osoitteesta keikkapaikalle ja takaisin – huomioi tämä, kun määrität hinnat. Hinnoittelu on maakuntakohtaista: hinnat säädetään kartan tai listan avulla.",
  tips: [
    "Lisää setti pudotusvalikosta; rivi lisäytyy vain valittuun maakuntaan.",
    "Anna setille hinta – vain hinnoitellut setit näkyvät profiilissa.",
    "Tyhjennä poistaa hinnan, mutta jättää setin riville.",
    "Poista setti poistaa rivin kokonaan tältä maakunnalta.",
    "0 € on sallittu hinta; tyhjä kenttä piilottaa setin tältä alueelta.",
    "Tallenna, kun olet valmis – setit ja hinnat päivittyvät kerralla."
  ],
}),


  social: () => ({
    title: "SOSIAALINEN MEDIA",
    subtitle:
      "Lisää some-linkisi, jätä tyhjäksi jos niitä ei ole",
    tips: [
      // "URL:it voivat olla ilman https:// — lisäämme sen automaattisesti.",
    ],
  }),
};



// jälkeen:
function getBandCopy(active, user) {
  if (active === "basic") return TAB_COPY.basic(user);
  if (active === "media") return TAB_COPY.media();
  if (active === "calendar") return TAB_COPY.calendar();
  if (active === "sets") return TAB_COPY.sets?.();
  if (active === "pricing") return TAB_COPY.pricing();
  if (active === "social") return TAB_COPY.social();
  return TAB_COPY.basic(user);
}

function RatingBadge({ band, compact = false }) {
  const avgRaw = Number(band?.rating?.average ?? band?.average_rating);
  const avg = Number.isFinite(avgRaw) ? Math.min(5, Math.max(0, avgRaw)) : 0;

  const totalRaw =
    Number(band?.rating?.total ?? band?.reviews_count ?? band?.total_reviews);
  const total = Number.isFinite(totalRaw)
    ? Math.max(0, Math.floor(totalRaw))
    : Array.isArray(band?.reviews)
    ? band.reviews.filter((r) => r?.is_published !== false).length
    : 0;

  const full = Math.floor(avg);
  const half = avg - full >= 0.5;

   if (compact) {
   return (
     <div className="inline-flex items-center gap-1">
       <span className="text-xs font-semibold opacity-90">{avg.toFixed(1)}</span>
       <div className="flex">
         {Array.from({ length: full }).map((_, i) => (
           <FaStar key={`f${i}`} className="w-3.5 h-3.5 text-yellow-400" />
         ))}
         {half && (
           <span className="relative inline-block w-3.5 h-3.5">
             <FaRegStar className="text-gray-400 absolute inset-0" />
             <FaStar className="text-yellow-400 absolute inset-0" style={{ clipPath: "inset(0 50% 0 0)" }} />
           </span>
         )}
         {Array.from({ length: 5 - full - (half ? 1 : 0) }).map((_, i) => (
           <FaRegStar key={`e${i}`} className="w-3.5 h-3.5 text-gray-400" />
         ))}
       </div>
       <span className="text-[11px] opacity-60">({total})</span>
     </div>
   );
 }


  return (
    <div className="mt-3 w-full flex justify-center items-center">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-xl bg-white/90 dark:bg-black/10 dark:ring-white/10 shadow-md">
        <div className="flex items-baseline gap-1">
          <span className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm font-semibold">
            {avg.toFixed(1)}
          </span>
          <span className="dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-70">
            ({total})
          </span>
        </div>
        <div className="flex">
          {Array.from({ length: full }).map((_, i) => (
            <FaStar key={`f${i}`} className="text-yellow-400" />
          ))}
          {half && (
            <span className="relative inline-block w-4 h-4">
              <FaRegStar className="text-gray-400 absolute inset-0" />
              <FaStar
                className="text-yellow-400 absolute inset-0"
                style={{ clipPath: "inset(0 50% 0 0)" }}
              />
            </span>
          )}
          {Array.from({ length: 5 - full - (half ? 1 : 0) }).map((_, i) => (
            <FaRegStar key={`e${i}`} className="text-gray-400" />
          ))}
        </div>
      </div>
    </div>
  );
}

function UserAccountPanel({ user, bands = [], onOpenPublic, exposeSave, onOpenDeleteAccount }) {
  const [name, setName] = useState(user?.display_name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  useEffect(() => {
    setName(user?.display_name || "");
    setPhone(user?.phone || "");
    setChanged(false);
  }, [user?.display_name, user?.phone]);

  const onPhoneChange = (v) => {
    const s = String(v).replace(/[^\d()+\-\s]/g, ""); // sama regex-henki kuin backissa
    setPhone(s);
    setChanged(true);
  };

  const save = async () => {
    const payload = {};
    if (name.trim() !== (user?.display_name || "")) payload.display_name = name.trim();
    if ((phone || "").trim() !== (user?.phone || "")) payload.phone = (phone || "").trim();

    if (!Object.keys(payload).length) {
      toast.info("Ei muutoksia tallennettavaksi.", { containerId: "global" });
      return;
    }

    setBusy(true);
    try {
      await api.auth.updateMe(payload);
      toast.success("Profiili tallennettu.", { containerId: "global" });
      setChanged(false);
    } catch (e) {
      toast.error(e?.payload?.error === "invalid_input" ? "Tarkista kentät." : "Tallennus epäonnistui.", { containerId: "global" });
    } finally {
      setBusy(false);
    }
  };

  // Jos haluat käyttää yläreunan yleistä "Tallenna"-ohjausta myöhemmin:
  useEffect(() => { exposeSave?.(save); }, [exposeSave, name, phone]); // eslint-disable-line

  return (
    <div className="relative">
      <div className="mx-auto w-full max-w-4xl">
        <div className="p-2">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <h3 className="dark:text-white/90 text-black/90 font-customFont2 text-lg mb-1 font-bold mt-6">
                Profiilin tiedot
              </h3>

              <label className="block text-xs opacity-80 mb-1">Nimi</label>
              <input
                type="text"
                value={name}
                onChange={(e)=>{ setName(e.target.value); setChanged(true); }}
                className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 dark:text-white/90"
              />

              <label className="block text-xs opacity-80 mt-3 mb-1">Sähköposti</label>
              <input
                type="email"
                value={user?.email || ""}
                readOnly
                className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 dark:text-white/90 opacity-80"
              />

              <label className="block text-xs opacity-80 mt-3 mb-1">Puhelinnumero</label>
              <input
                type="tel"
                value={phone}
                onChange={(e)=>onPhoneChange(e.target.value)}
                placeholder="+358 …"
                className="w-full h-10 px-3 rounded-lg bg-white dark:bg-black/50 border border-black/10 dark:border-white/10 dark:text-white/90"
              />

              <div className="mt-4 mb-6 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-4">
                <NeonButton
                  type="button"
                  variant="primary"
                  onClick={save}
                  disabled={busy || !changed}
                  className="scale-[0.80] md:scale-100 font-semibold"
                >
                  {busy ? "Tallennetaan…" : "Tallenna"}
                </NeonButton>

                <NeonButton
                  type="button"
                  variant="primary"
                  className="scale-[0.80] md:scale-100 font-semibold"
                  onClick={() => setPwdOpen(true)}
                >
                  Vaihda salasana
                </NeonButton>

                <NeonButton
                  type="button"
                  variant="secondary"
                  onClick={onOpenDeleteAccount}
                  className="scale-[0.80] md:scale-100 font-semibold"
                >
                  Poista tili
                </NeonButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}

// ---------------- Pääkomponentti ----------------

export default function Dashboard() {
  const { user } = useAuth();
  const needsAccepts = !!user && (!user.accepted_terms_at || !user.accepted_privacy_at);
  const navigate = useNavigate();
  const location = useLocation();
  const [bands, setBands] = useState([]);

    // 🔥 Web-dev -popup: näytetään vain kirjautuneelle
  const [showWebDevPromo, setShowWebDevPromo] = useState(false);
  
// Kirjautumisen state talteen kerran, ettei tarvitse lisätä location riippuvuudeksi
const fromLoginRef = useRef(location.state?.welcomeFromLogin === true);
const welcomeShownRef = useRef(false);

// Estä placeholder-vilkahdus: muista viimeisin toimiva kuva per bändi
const imageCacheRef = useRef(new Map()); // id -> url
  
// Kuinka monta korttia on tällä hetkellä (auttaa viimeisen poiston logiikassa)
const bandsCountRef = useRef(0);

useEffect(() => {
  bandsCountRef.current = bands.length;
}, [bands]); // riippuvuus: bands (ei bands.length)

  const [bandLimits, setBandLimits] = useState({
    max_bands: 1,
    bands_count: 0,       // omistettujen määrä
    can_add_band: false,
  });
  const [loadingBand, setLoadingBand] = useState(false);
  const [loadingBands, setLoadingBands] = useState(true);

  const noBands = !loadingBands && bands.length === 0;

  // --- State ---
  // Alusta bändi + tab suoraan URL:stä, jotta ei vilahda käyttäjäpaneelin kautta
// --- State ---
// Alusta bändi + tab suoraan URL:stä
const [selected, setSelected] = useState(() => {
  const qsInit = new URLSearchParams(location.search || "");
  const bandParamRaw = (qsInit.get("band") || "").trim();
  const bandParam = bandParamRaw.toLowerCase();

  // user → mene käyttäjänäkymään
  if (!bandParam || bandParam === "user") {
    return { type: "user", id: null };
  }

  // band id/slug
  return { type: "band", id: bandParamRaw === "auto" ? null : bandParamRaw };
});

  const selectedBandId = selected.type === "band" ? selected.id : null;
  const [band, setBand] = useState(null);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [checklistVersion, setChecklistVersion] = useState(0);
  const bumpChecklist = useCallback(() => setChecklistVersion(v => v + 1), []);


  // Aktiivinen välilehti: oletus käyttäjälle
  // Aktiivinen välilehti: jos tullaan tab-paramilla → käytä sitä heti
const [active, setActive] = useState(() => {
  const qsInit = new URLSearchParams(location.search || "");
  const tabInit = (qsInit.get("tab") || "").toLowerCase();
  const bandParamRaw = (qsInit.get("band") || "").trim();
  const ssLast = (() => {
    try { return (sessionStorage.getItem(`${LAST_TAB_PREFIX}${bandParamRaw}`) || "").toLowerCase(); }
    catch { return ""; }
  })();
  if (selected.type === "band") {
    return ALLOWED_TABS.has(tabInit)
      ? tabInit
      : (ALLOWED_TABS.has(ssLast) ? ssLast : "basic");
  }
  return "uprofile";
});
  

  // ───────────────────────────────────────────────────────────────
  // Onboardingista tullessa: ohita mahdolliset /setup-ohjaukset
  // ja pakota käyttäjänäkymä (Tiliasetukset) kerran.
  useEffect(() => {
    try {
      const key = "dashboard.skipSetupOnce";
      if (sessionStorage.getItem(key) === "1") {
        sessionStorage.removeItem(key); // käytä vain kerran
        setSelected({ type: "user", id: null });
        setActive("uprofile");
        if (location.pathname === "/dashboard") {
          const qs = new URLSearchParams(location.search || "");
          const band = (qs.get("band") || "").toLowerCase();
          const tab  = (qs.get("tab")  || "").toLowerCase();
          if (band !== "user" || tab !== "uprofile") {
            navigate("/dashboard?band=user&tab=uprofile", { replace: true });
          }
        } else {
          navigate("/dashboard?band=user&tab=uprofile", { replace: true });
        }
      }
    } catch {}
  }, [navigate, location.pathname, location.search]);
  // ───────────────────────────────────────────────────────────────

  // Action bar -tila nostettuna ylös → näkyy bänditabeissa
  const [isPublished, setIsPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadBandsError, setLoadBandsError] = useState(false);
  const [confirmPublishOpen, setConfirmPublishOpen] = useState(false);
  // ⬇️ PIILOTUKSEN varmistus (PUUTTUI)
const [confirmUnpublishOpen, setConfirmUnpublishOpen] = useState(false);
  const [confirmDeleteAccountOpen, setConfirmDeleteAccountOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
// Poiston varmistus (kortti pois tililtä)
const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
const [removing, setRemoving] = useState(false);
const [bandToRemove, setBandToRemove] = useState(null);

// ⬇️ UUSI: kun käyttäjä on viimeinen omistaja → tarjotaan koko bändin poistoa
const [confirmDeleteBandOpen, setConfirmDeleteBandOpen] = useState(false);
const [deletingBand, setDeletingBand] = useState(false);

// ⬇️ UUSI: review-check → voidaanko lähettää tarkistukseen?
const [reviewReady, setReviewReady] = useState(false);
const [reviewLoading, setReviewLoading] = useState(false);

const openRemoveDialog = useCallback((bandLike) => {
  setBandToRemove(bandLike || null);
  setConfirmRemoveOpen(true);
}, []);

const deleteAccount = async () => {
  setDeletingAccount(true);
  try {
    await api.auth.deleteAccount();          // 204/205 tuettu fetch-helperissä
    try { await api.auth.logout(); } catch {}
    window.location.assign("/");             // pidä tämä
    return;                                  // ei suljeta modaalia erikseen
  } catch (e) {
    const status = Number(e?.status);
    const code = String(e?.payload?.error || e?.payload?.code || "").toLowerCase();

    // 409: tilillä on vielä riippuvuuksia (omistetut kortit / pidot / keikat)
    if (status === 409 && (code === "owns_bands" || code === "account_dependencies")) {
      const d = e?.payload?.details || {};
      const num   = (v) => (Number.isFinite(+v) ? +v : 0);
      const owned = num(d.owned_bands ?? d.bands ?? 0);
      const holds = num(d.active_holds ?? d.band_active_holds ?? 0);
      const gigs  = num(d.upcoming_gigs ?? d.band_upcoming_gigs ?? 0);

      const parts = [];
      if (owned) parts.push(`${owned} omistettua korttia`);
      if (holds) parts.push(`${holds} aktiivista pitoa`);
      if (gigs)  parts.push(`${gigs} tulevaa keikkaa`);
      const detail = parts.join(" ja ");

     toast.error(
      `Yrititkö karata? Tilillä on vielä ${detail || "riippuvuuksia"}. Hoida keikat ja pidot, poista kortti — sitten vasta tili.`,
      { containerId: "global", autoClose: 8000 }
    );

      return;
    }

    // Muut virheet
    const msg = e?.payload?.message || e?.message || "Tilin poisto epäonnistui.";
    toast.error(msg, { containerId: "global" });
  } finally {
    setDeletingAccount(false);
    // Modaalia EI suljeta virheessä; käyttäjä voi yrittää uudelleen tai perua
  }
};

const doRemoveBand = useCallback(async () => {
  if (!bandToRemove) return;

  const id = String(bandToRemove.band_id ?? bandToRemove.id); // ✅ puuttui
  const wasLast = bandsCountRef.current === 1;                 // ✅ ennen poistoa

  try {
    setRemoving(true);

    // Poista VAIN oma jäsenyys; koko bändin poisto tehdään erillisessä dialogissa
    if (api?.memberships?.leave) {
      await api.memberships.leave(id);
    } else if (api?.bands?.removeMembership) {
      await api.bands.removeMembership(id);
    } else if (api?.bands?.leave) {
      await api.bands.leave(id);
    } else {
      throw new Error("Membership-leave endpoint puuttuu");
    }

    // Päivitä UI-lista
    setBands(prev => prev.filter(b => String(b.band_id ?? b.id) !== id));

    // Jos poistettu oli valittuna → takaisin käyttäjänäkymään
    if (String(selectedBandId) === id) {
      setSelected({ type: "user", id: null });
      setActive("uprofile");
    }

    // Estä kertaluonteisesti setupiin ohjaus heti viimeisen poiston jälkeen
    if (wasLast) {
      try { sessionStorage.setItem("dashboard.skipSetupOnce", "1"); } catch {}
    }

    toast.success("Kortti poistettu tililtä.", { containerId: "global" });
    setConfirmRemoveOpen(false);
    setBandToRemove(null);
  } catch (e) {
    console.error("remove band failed", e);

    const rawCode = e?.payload?.error || e?.payload?.code || e?.code || "";
    const code = String(rawCode).toLowerCase();
    const status = Number(e?.status);

    const conflictCodes = new Set([
      "band_has_busy_calendar","band_has_confirmed_gigs","busy_calendar",
      "has_confirmed_events","has_upcoming_confirmed","has_bookings",
      "has_active_holds","cannot_delete_with_bookings","cannot_leave_with_bookings",
      "cannot_remove_with_bookings",
    ]);

    if (code === "last_owner_cannot_leave") {
      setConfirmRemoveOpen(false);
      setConfirmDeleteBandOpen(true);
      return;
    }

    const num = v => (Number.isFinite(+v) ? +v : 0);
    const p = e?.payload || {};
    const nConf = num(p.confirmed_upcoming) || num(p.upcoming_confirmed) || num(p.confirmed) || num(p.confirmed_gigs) || 0;
    const nHold = num(p.active_holds) || num(p.holds) || num(p.pending_inquiries) || num(p.active_requests) || 0;

    const isCalendarBlock = conflictCodes.has(code) || status === 409 || status === 422;

    if (isCalendarBlock) {
      const detail = [
        nConf ? `${nConf} vahvistettua keikkaa` : null,
        nHold ? `${nHold} aktiivista pitoa` : null,
      ].filter(Boolean).join(" ja ");

      toast.error(
        `Yrititkö karata? Kalenterissasi ${detail || "on vielä aktiivisia varauksia/pitoja"}. Hoidathan ne ensin!`,
        { containerId: "global" }
      );
    } else if (status === 404 || status === 405) {
      console.warn("Membership-leave -endpoint ei ole käytettävissä tässä ympäristössä.");
      toast.error(
        "Poisto on pois käytöstä tässä ympäristössä. Tämä on meidän heiniä, ei sinun.",
        { containerId: "global" }
      );
    } else {
      toast.error("Poisto epäonnistui. Yritä hetken kuluttua.", { containerId: "global" });
    }

  } finally {
    setRemoving(false);
  }
}, [bandToRemove, selectedBandId]);


// ⬇️ UUSI: poista koko esiintyjä (admin-only tai kun endpoint sallii)
const doDeleteBand = useCallback(async () => {
  if (!bandToRemove) return;
  const id = String(bandToRemove.band_id ?? bandToRemove.id);
  try {
    setDeletingBand(true);
    await api.bands.delete(id);

    // ✅ poista UI-listasta
    setBands((prev) => prev.filter((b) => String(b.band_id ?? b.id) !== id));

    // jos valittuna → takaisin käyttäjänäkymään
    if (String(selectedBandId) === id) {
      setSelected({ type: "user", id: null });
      setActive("uprofile");
    }

    toast.success("Esiintyjä poistettu.", { containerId: "global" });
    setConfirmDeleteBandOpen(false);
    setBandToRemove(null);
  } catch (e) {
    const code = String(e?.payload?.error || e?.payload?.code || e?.code || "").toLowerCase();
    const status = Number(e?.status);
    const num = (v) => (Number.isFinite(+v) ? +v : 0);
    const p = e?.payload || {};
    const nConf =
      num(p.confirmed_upcoming) || num(p.upcoming_confirmed) || num(p.confirmed) || num(p.confirmed_gigs) || 0;
    const nHold =
      num(p.active_holds) || num(p.holds) || num(p.pending_inquiries) || num(p.active_requests) || 0;

if (
  status === 409 || status === 422 ||
  ["has_confirmed_events","has_bookings","has_active_holds","busy_calendar",
   "cannot_delete_with_bookings","band_has_active_requests"].includes(code)
) {
  const detail = [
    nConf ? `${nConf} vahvistettua keikkaa` : null,
    nHold ? `${nHold} aktiivista pitoa` : null,
  ]
    .filter(Boolean)
    .join(" ja ");

  toast.error(
    `Yrititkö karata? Kalenterissa on vielä ${detail || "aktiivisia varauksia/pitoja"}. Hoidathan nämä ensin, sitten voit poistaa korttisi.`,
    { containerId: "global" }
  );

  } else {
    toast.error(
      "Poisto epäonnistui. Tekniikka nikotteli – kokeile hetken päästä uudelleen.",
      { containerId: "global" }
    );
  }
  } finally {
    setDeletingBand(false);
  }
  }, [bandToRemove, selectedBandId]);


const [submittingReview, setSubmittingReview] = useState(false);

// UI:n “pending-ikkuna” (voimassa vaikka backi palauttaisi vielä draftin)
const [pendingUntil, setPendingUntil] = useState(null);

const serverStatus = band?.status
  ? String(band.status).toLowerCase()
  : "";

const effectiveStatus = serverStatus || (isPublished ? "approved" : "draft");

// erota selkeästi hylätty
const isRejected = effectiveStatus === "rejected";
const isApprovedHidden = effectiveStatus === "approved" && !isPublished;

const isPending =
  !isRejected &&
  !isPublished &&
  (
    effectiveStatus === "pending" ||
    (pendingUntil && pendingUntil > Date.now())
  );

const submitForReview = useCallback(async () => {
  if (!band?.id) return false;
  try {
    setSubmittingReview(true);

    // Pyyntö ja serverin palauttama päättymisaika (jos mukana)
    const res = await api.submitForReview(band.id);

    const expiresAtIso =
      res?.expires_at ||                      // ensisijainen
      res?.review_token_expires_at ||         // vaihtoehtoinen nimi
      res?.band?.review_token_expires_at ||   // joissain vastauksissa band-objektin sisällä
      null;

    // CRA/React env – lisää tarv. .env: REACT_APP_REVIEW_TTL_HOURS=72
    const fallbackHours = Number(process.env.REACT_APP_REVIEW_TTL_HOURS ?? 72);

    // merkitse odottamaan hyväksyntää
    setBand(v => (v ? { ...v, status: "pending", is_published: false, review_token_expires_at: expiresAtIso ?? v?.review_token_expires_at } : v));
    // pidä UI pending-tilassa vaikka palvelin palauttaisi vielä draftin
    {
      const expMs = expiresAtIso
        ? new Date(expiresAtIso).getTime()
        : Date.now() + fallbackHours * 3600e3;
      setPendingUntil(expMs);
      try { sessionStorage.setItem(`bbx.pending.${band.id}`, String(expMs)); } catch {}
    }
    setIsPublished(false);
    setConfirmPublishOpen(false);
      toast.success(
        "Profiili lähetettiin tarkistettavaksi. Ilmoitamme sähköpostilla, kun päätös on tehty.",
        { containerId: "global", toastId: "review-submitted", icon: "📬", autoClose: 7000 }
      );

    return true;
  } catch (e) {
    console.warn("submit-for-review failed", { status: e?.status, payload: e?.payload });
    const miss = e?.payload?.missing;
    if (Array.isArray(miss) && miss.length) {
      toast.info(`Täydennä vielä: ${miss.join(", ")}`, {
        containerId: "global",
        autoClose: 7000,
      });
    } else {
      toast.error("Lähetys epäonnistui.", { containerId: "global" });
    }
    return false;
  } finally {
    setSubmittingReview(false);
  }
}, [band?.id]);

// ⬇️ UUSI: hae review-check ja laske, onko kaikki valmista
const refreshReviewReady = useCallback(async () => {
  if (!band?.id) { setReviewReady(false); return; }
  setReviewLoading(true);
  try {
    const res = await (api.reviewCheck
      ? api.reviewCheck(band.id)
      : fetch(`/api/bands/${band.id}/review-check`, { credentials: "include" }).then(r => r.json()));

    // Tuetaan res, res.data tai res.result -muotoja
    const payload = res?.data ?? res?.result ?? res ?? {};
    const missing = Array.isArray(payload?.missing) ? payload.missing : [];
    const okFlag = typeof payload?.ok === "boolean" ? payload.ok : null;

    // Jos 'ok' puuttuu, päättele puuttuvien perusteella
    const ok = okFlag !== null ? okFlag : missing.length === 0;
    setReviewReady(!!ok);
  } catch {
    // Virhetilassa pelataan varman päälle → ei voi lähettää
    setReviewReady(false);
  } finally {
    setReviewLoading(false);
  }
}, [band?.id]);

// ⬇️ UUSI: pidä “valmius” ajan tasalla
useEffect(() => {
  refreshReviewReady();
}, [refreshReviewReady, band?.id, checklistVersion]);

// ⬇️ Päivitä per-bändi “viimeisin tab” aina kun aktiivinen tab vaihtuu
useEffect(() => {
  if (selected.type !== "band" || !selectedBandId) return;
  try {
    sessionStorage.setItem(`${LAST_TAB_PREFIX}${selectedBandId}`, active);
  } catch {}
}, [active, selected.type, selectedBandId]);

  const saveHandlerRef = useRef(null); // BasicTab/UserTab rekisteröi tänne tallennusfunktion

  // --- Lasketut oikeudet (tarvitaan showSaveBtn:lle) ---
  const currentId = String(band?.id ?? selectedBandId ?? "");
  const myMembership =
    bands.find(b => String(b.band_id ?? b.id) === currentId) || null;

  const canEdit =
    selected.type === "band" &&
    (myMembership?.role || "").toLowerCase() === "owner";

  // Julkaisun oikeus: sallitaan klikkaus aina, serveri tarkistaa.
  // Pending-tilassa nappi estetään erikseen.
  const canPublish = canEdit;

  // Ei autosave-tabeja: Kalenteri käyttää nyt sticky-Tallenna -nappia
  const AUTO_SAVE_TABS = new Set([]);

  // Kun aktiivinen tabi on rekisteröinyt tallennusfunktion
  const [saveReady, setSaveReady] = useState(false);

  // Yhdistetty ehto Tallenna-napille
  const showSaveBtn =
    selected.type === "band" &&
    !!band &&
    canEdit &&
    !AUTO_SAVE_TABS.has(active) &&
    !!saveHandlerRef.current &&
    saveReady;

useEffect(() => {
  if (Array.isArray(bands) && bands.length > 0) {
    try { localStorage.setItem("dashboard.hadBandsOnce", "1"); } catch {}
  }
}, [bands]);

// Salli siirtyminen tiliasetuksiin URLilla (esim. breadcrumb-linkki)
useEffect(() => {
  const qs = new URLSearchParams(location.search || "");
  const bandParamRaw = (qs.get("band") || "").trim();
  const bandParamLower = bandParamRaw.toLowerCase();
  const tabParam = (qs.get("tab") || "").trim().toLowerCase();

  if (bandParamLower === "user") {
    setSelected(prev => (prev.type === "user" ? prev : { type: "user", id: null }));
    setActive(prev => (prev === "uprofile" ? prev : "uprofile"));
    return;
  }

  if (bandParamRaw) {
    const nextTab = ALLOWED_TABS.has(tabParam) ? tabParam : "basic";
    setSelected({ type: "band", id: bandParamRaw === "auto" ? null : bandParamRaw });
    setActive(nextTab);
  }
}, [location.search]);

// Pikapolku: /dashboard?band=new → suoraan lisäkortti-wizardiin
useEffect(() => {
  const qs = new URLSearchParams(location.search || "");
  if ((qs.get("band") || "").toLowerCase() === "new") {
    navigate("/setup/add", { replace: true });
  }
}, [location.search, navigate]);

// Nollaa tallennus-valmius kun konteksti vaihtuu → lapsi rekisteröi handlerin uudelleen
useEffect(() => {
  setSaveReady(false);
  saveHandlerRef.current = null;
}, [active, selected.type, selectedBandId]); 

useEffect(() => {
  if (!loadingBands && bands.length > 0) {
    try {
      sessionStorage.removeItem("setup.completed");
      sessionStorage.removeItem("setup.redirect");
    } catch {}
  }
}, [loadingBands, bands.length]);

// Pollaa tilaa vain kun ollaan “pending” (status tai pendingUntil)
useEffect(() => {
  if (!band?.id || !isPending) return;

  let stop = false;
  const ab = new AbortController();

  const fetchStatus = async () => {
    try {
      const data = await api.bandByIdOrSlug(band.id, { signal: ab.signal });
      if (stop) return;
      const b = data?.band || data;
      setBand(b);
      setIsPublished(!!b?.is_published);

      // Päivitä/pura pending-ikkuna palvelimen kenttien perusteella
      const srvExp = b?.review_token_expires_at || b?.review_expires_at || null;
      if (srvExp) {
        const ms = new Date(srvExp).getTime();
        setPendingUntil(ms);
        try { sessionStorage.setItem(`bbx.pending.${b.id}`, String(ms)); } catch {}
      } else {
        const s = (b?.status || "").toLowerCase();
        if (b?.is_published || s === "approved" || s === "rejected") {
          setPendingUntil(null);
          try { sessionStorage.removeItem(`bbx.pending.${b.id}`); } catch {}
        }
      }
    } catch {}
  };

  const onFocus = () => fetchStatus();

  const id = setInterval(fetchStatus, 20000);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onFocus);

  fetchStatus(); // heti

  return () => {
    stop = true;
    clearInterval(id);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onFocus);
    ab.abort();
  };
}, [band?.id, isPending]);


// Voi olla hetken null ennen kuin AuthContext ehtii täyttyä
const u = user ?? {};
const copy =
  selected.type === "user"
    ? TAB_COPY.user(u, bandLimits, bands)
    : getBandCopy(active, u);

  // --- Web-sivumainos: näytetään vain kirjautuneelle, joka on hyväksynyt ehdot ---
  useEffect(() => {
    if (!ENABLE_WEBDEV_PROMO) return;
    if (!user || needsAccepts) return;

    try {
      // jos käyttäjä on kerran valinnut "Älä näytä tätä enää"
      if (localStorage.getItem("webdev_promo_dismissed") === "1") return;

      // ei spämmätä: kerran per sessio
      if (sessionStorage.getItem("webdev_promo_shown") === "1") return;

      setShowWebDevPromo(true);
      sessionStorage.setItem("webdev_promo_shown", "1");
    } catch {
      // jos storage ei toimi (esim. incognito-rajoitus) → näytetään kerran
      setShowWebDevPromo(true);
    }
  }, [user, needsAccepts]);


  // 3) Hae omat bändit
  useEffect(() => {
    if (!user || needsAccepts) return; // älä potki setupiin ennen hyväksyntöjä
    let mounted = true;

    (async () => {
      setLoadingBands(true);
      setLoadBandsError(false);
      try {
        const res = await api.auth.meBands();

        const list =
          Array.isArray(res?.bands) ? res.bands :
          Array.isArray(res?.items) ? res.items :
          Array.isArray(res?.memberships) ? res.memberships :
          Array.isArray(res) ? res : [];

        const items = list.map((m) => {
          const b = m.band || m;
          const id = b.band_id ?? b.id;
          return {
            // huom: jäsenyys ensin, bändi päälle
            ...m,
            ...b,
            id,
            band_id: id,
            name: b.name ?? b.band_name ?? m.name ?? m.band_name ?? `#${id}`,
            role: m.role ?? b.role ?? "member",
          };
        });

        if (!mounted) return;
             // --- TOASTIT: esikatselusta paluu vs. login-tervetulo ---
        {
          // 1) Paluu esikatselusta (näytetään aina kun avain on asetettu)
          let fromPreview = false;
          try { fromPreview = sessionStorage.getItem(PREVIEW_TOAST_KEY) === "1"; } catch {}
          if (fromPreview) {
            try { sessionStorage.removeItem(PREVIEW_TOAST_KEY); } catch {}
            requestAnimationFrame(() => {
              toast.success("Hyvältä näyttää! Jatketaanko vielä muokkausta vai laitetaanko liveksi?", {
                containerId: "global", autoClose: 5000, toastId: "after-preview-hint"
              });

            });

          // ⬇️ Palauta aina samaan bändiin ja samaan tabiinkin
            try {
              const retBand = sessionStorage.getItem(PREVIEW_RETURN_BAND_KEY);
              const retTabRaw = (sessionStorage.getItem(PREVIEW_RETURN_TAB_KEY) || "").toLowerCase();
              if (retBand) {
                const remembered = (sessionStorage.getItem(`${LAST_TAB_PREFIX}${retBand}`) || "").toLowerCase();
                const retTab = ALLOWED_TABS.has(retTabRaw)
                  ? retTabRaw
                  : (ALLOWED_TABS.has(remembered) ? remembered : "basic");
                navigate(`/dashboard?band=${encodeURIComponent(retBand)}&tab=${retTab}`, { replace: true });
              }
              sessionStorage.removeItem(PREVIEW_RETURN_BAND_KEY);
              sessionStorage.removeItem(PREVIEW_RETURN_TAB_KEY);
            } catch {}

          }

          
          // 2) Tervetulo-toast vain kerran sessiossa
          else if (!welcomeShownRef.current) {
            const ssKey = "showWelcomeToast"; // tämä tulee loginista
            let viaSession = false;
            try { viaSession = sessionStorage.getItem(ssKey) === "1"; } catch {}
            const fromLogin = !!fromLoginRef.current;

            if (viaSession || fromLogin) {
              welcomeShownRef.current = true; // estä duplikaatit tällä mountilla
              if (viaSession) {
                try { sessionStorage.removeItem(ssKey); } catch {}
              }
              const msg =
                items.length > 0
                  ? "Tervetuloa hallintapaneeliin!"
                  : "Tervetuloa hallintaan! Ensimmäiseksi luo esiintyjäkortti painamalla “Luo esiintyjäkortti”.";
              requestAnimationFrame(() => {
                toast.success(msg, { containerId: "global", autoClose: 5500 });
              });
              if (fromLogin) navigate(".", { replace: true, state: {} });
            }
          }
        }
        setBands(items);

        // meta backendiltä (ks. /auth/me/bands diff aiemmin)
        const meta = res?.meta || {};
        const max  = Number(meta.max_bands ?? 1);
        // fallback: lasketaan owners määrä localisti jos meta puuttuu
        const owners = Number(
          meta.bands_count ??
          items.filter(m => String(m.role || '').toLowerCase() === 'owner').length
        );
        setBandLimits({
          max_bands: max,
          bands_count: owners,
          can_add_band: owners < max,
        });

        // --- 🔧 RIKASTUS: nouda kuvat niille joilta puuttuu ---
        const needs = items.filter(
          (b) => !(b.profile_image || b.profile_image_url || b.placeholder_image || b.placeholder_image_url)
        );
        if (needs.length > 0) {
          const ab = new AbortController();
          try {
            const details = await Promise.all(
              needs.map((b) =>
                api.bandByIdOrSlug(b.band_id ?? b.id, { signal: ab.signal }).catch(() => null)
              )
            );

            const byId = new Map();
            for (const d of details) {
              const band = d?.band || d;
              if (!band) continue;
              const id = String(band.band_id ?? band.id);
              byId.set(id, band);
            }

            if (!mounted) return;
            setBands((prev) =>
              prev.map((b) => {
                const full = byId.get(String(b.band_id ?? b.id));
                if (!full) return b;
                return {
                  ...b,
                  // kopioidaan vain mitä kortti/hero tarvitsee
                  profile_image: full.profile_image ?? b.profile_image,
                  profile_image_url: full.profile_image_url ?? b.profile_image_url,
                  placeholder_image: full.placeholder_image ?? b.placeholder_image,
                  placeholder_image_url: full.placeholder_image_url ?? b.placeholder_image_url,
                  slug: full.slug ?? b.slug,
                };
              })
            );
          } finally {
            // ab.abort(); // jos haluat perua manuaalisesti unmountissa
          }
        }
        // --- 🔧 RIKASTUS LOPPU ---
      } catch (e) {
        console.error("/auth/me/bands fail", e);
        if (mounted) setLoadBandsError(true);
      } finally {
        if (mounted) setLoadingBands(false);
      }
    })();

    return () => {
      mounted = false;
    };
}, [user, needsAccepts, navigate]);

  // 4) Kun konteksti vaihtuu → resetoi vain käyttäjänäkymässä
  useEffect(() => {
    if (selected.type === "user") {
      setActive("uprofile");
    }
    // Jos siirrytään bändiin, älä yliaja aktiivista tabia:
    // - URL-parametrit tai korttiklikkaus asettavat tabin itse.
  }, [selected.type]);

// --- 5) Hae valitun bändin tiedot (vain kun bändi valittu)
useEffect(() => {
  if (selected.type !== "band" || !selectedBandId) {
    setBand(null);
    setLoadingBand(false);
    return;
  }
  const ab = new AbortController();
  setLoadingBand(true);
  (async () => {
    try {
      const data = await api.bandByIdOrSlug(selectedBandId, { signal: ab.signal });
      const loaded = data?.band || data || null;
      setBand(loaded);
       try {
        const t = loaded?.review_token || loaded?.preview_token;
        if (t) sessionStorage.setItem("bbx.previewToken", t);
      } catch {}
      // ✅ UUSI: jos valinta tehtiin slugilla, vaihda tilaan bändin ID
      if (loaded && String(selectedBandId) !== String(loaded.id)) {
        setSelected({ type: "band", id: String(loaded.id) });
      }
    } catch (e) {
      /* ... entinen koodi ... */
    } finally {
      setLoadingBand(false);
    }
  })();
  return () => ab.abort();
}, [selected.type, selectedBandId]);

  // Päivitä korttilista ja slotit aina kun selainikkuna saa fokuksen
  useEffect(() => {
    let alive = true;

    const refresh = async () => {
      try {
        const res = await api.auth.meBands();

        const list =
          Array.isArray(res?.bands) ? res.bands :
          Array.isArray(res?.items) ? res.items :
          Array.isArray(res?.memberships) ? res.memberships :
          Array.isArray(res) ? res : [];

        const items = list.map((m) => {
          const b = m.band || m;
          const id = b.band_id ?? b.id;
          return {
            ...m,
            ...b,
            id,
            band_id: id,
            name: b.name ?? b.band_name ?? m.name ?? m.band_name ?? `#${id}`,
            role: m.role ?? b.role ?? "member",
          };
        });

        if (!alive) return;
        setBands(items);

        const meta = res?.meta || {};
        const max  = Number(meta.max_bands ?? 1);
        const owners = Number(
          meta.bands_count ??
          items.filter((m) => String(m.role || '').toLowerCase() === 'owner').length
        );

        setBandLimits({
          max_bands: max,
          bands_count: owners,
          can_add_band: owners < max,
        });
      } catch {}
    };

    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      alive = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  // Nollaa mahdollinen esikatselukuva kun bändi vaihtuu
  useEffect(() => {
    setPendingProfile(null);
  }, [band?.id]);


  // Synkkaa julkaisutila kun bändi vaihtuu
  useEffect(() => {
    setIsPublished(!!band?.is_published);
  }, [band?.is_published]);

// Lataa pending-ikkuna kun bändi vaihtuu tai backi tarjoaa erääntymän
useEffect(() => {
  if (!band?.id) { setPendingUntil(null); return; }

  const status = (band?.status || "").toLowerCase?.() || "";
  const srvExp = band?.review_token_expires_at || band?.review_expires_at || null;

  // Jos profiili on jo hyväksytty / hylätty / julkaistu → EI enää pendingiä,
  // vaikka review_token_expires_at olisi jäänyt kantaan.
  if (band?.is_published || status === "approved" || status === "rejected") {
    setPendingUntil(null);
    try { sessionStorage.removeItem(`bbx.pending.${band.id}`); } catch {}
    return;
  }

  // Muuten käytä backendin antamaa erääntymää
  if (srvExp) {
    const ms = new Date(srvExp).getTime();
    setPendingUntil(ms);
    try { sessionStorage.setItem(`bbx.pending.${band.id}`, String(ms)); } catch {}
    return;
  }

  // Viimeinen fallback: sessionStorage
  try {
    const ms = Number(sessionStorage.getItem(`bbx.pending.${band.id}`) || 0);
    setPendingUntil(ms > Date.now() ? ms : null);
  } catch {
    setPendingUntil(null);
  }
}, [band?.id, band?.status, band?.is_published, band?.review_token_expires_at, band?.review_expires_at]);


const onTogglePublish = useCallback(() => {
  if (isPending || publishing || submittingReview) return;

  if (!isPublished) {
    // julkaisu → varmistusmodal
    setConfirmPublishOpen(true);
  } else {
    // piilotus → varmistusmodal
    setConfirmUnpublishOpen(true);
  }
}, [isPending, publishing, submittingReview, isPublished]);

const doUnpublish = useCallback(async () => {
  try {
    setPublishing(true);
    await api.publishBand(band.id, false);
    setIsPublished(false);
    setConfirmUnpublishOpen(false);
    toast.success("Profiili piilotettu.", { containerId: "global" });
  } catch (e) {
    toast.error("Piilotus epäonnistui.", { containerId: "global" });
  } finally {
    setPublishing(false);
  }
}, [band?.id]);



  // Päältä ohjattu "Tallenna" (UserTab / BasicTab)
  const handleSaveClick = useCallback(async () => {
    if (!saveHandlerRef.current) {
      toast.info("Tällä välilehdellä ei ole erillistä tallennusta.", {
        containerId: "global",
      });
      return;
    }
    setSaving(true);
    try {
      await saveHandlerRef.current();
    } finally {
      setSaving(false);
    }
  }, []);

  // --- SAFE BOOT OVERLAY ennen guardeja ---
  const hadSessionHint = (() => {
    try {
      return (
        localStorage.getItem("bbx_jwt") ||
        localStorage.getItem("bbx_refresh") ||
        localStorage.getItem("bbx_had_session") === "1"
      );
    } catch { return false; }
  })();
  const authBooting = (user === undefined || user === null) && !!hadSessionHint;
  if (authBooting) {
       return (
      <PageOverlayLoading
        label="Kirjaudutaan sisään…"
        bgImage={heroImg}
        blur={12} />
    );
  }

  // --- OPENING OVERLAY ennen guardeja ---
  const opening =
    loadingBands ||
    (selected.type === "band" && (!selectedBandId || loadingBand));
  if (opening) {
    return (
      <PageOverlayLoading
        label="Ladataan hallintaa…"
        bgImage={heroImg}
        blur={12} />
    );
  }

  // --- Guardit ---

  const lacksAccepts =
    !!user && (!user.accepted_terms_at || !user.accepted_privacy_at);
  if (lacksAccepts) {
   return <Navigate to="/onboarding" replace state={{ from: location }} />;
 }
  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h1 className="font-customFont2 dark:text-white/90 text-black/90 text-2xl font-semibold pb-4">Kirjautuminen vaaditaan</h1>
        <p>Sinulla ei näytä olevan aktiivista istuntoa.</p>
      </div>
    );
  }

// (siirretty ylemmäs: opening overlay tulee ennen guardeja)

  // ---------------- RENDER ----------------
  return (
   <div className="min-h-[70vh] max-w-5xl mx-auto px-2 pt-28 pb-10 md:pb-40">
      {/* Yläreunan hero-kuva – vain käyttäjänäkymässä */}
      {selected.type === "user" && (
        <HeroImageBanner
          className={`mb-6 ${DASH_HERO.hMobile} ${DASH_HERO.hDesktop} rounded-2xl`}
          src={DASH_HERO.image}
          alt="Oma tili – Buukkeri"
        />
      )}

      {/* Otsikko + kuva oikealla desktopissa */}
       <div className={
          selected.type === "user"
            ? "mb-6 grid grid-cols-1 gap-2 place-items-center text-left"

            : "mb-6 grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 items-start gap-2 p-2"
        }>
        <div className={ selected.type === "user"
          ? "mb-6 grid grid-cols-1 gap-2 items-start justify-items-start text-left"
          : "mb-6 grid grid-cols-1 md:grid-cols-1 items-start gap-2" }>

          {/* ---------- Title only (fixed height) ---------- */}
          <header className="">
            <h1
              className="font-customFont2 dark:text-white/90 text-black/90 xxxsm:text-xl text-2xl font-semibold pb-2
                         whitespace-nowrap overflow-hidden text-ellipsis min-h-[2.5rem]"
            >
              {copy.title}
            </h1>
          </header>

          {/* ---------- BÄNDINÄKYMÄN MURUPOLKU + TABIT ---------- */}
          {selected.type === "band" && band && (
            <div className="my-4 space-y-2">
              <Breadcrumbs
                items={[
                  { label: "Tiliasetukset", to: "/dashboard?band=user&tab=uprofile" },
                  { label: band?.name || "Esiintyjä" },
                ]}
              />

              {/* Vain bändin tabit (ei "Oma tiliä") */}
              <nav className="flex flex-wrap items-center gap-2 justify-center md:justify-start">
                {[
                  { key: "basic",    label: "Perustiedot" },
                  { key: "social",   label: "Some" },
                  { key: "media",    label: "Media" },
                  { key: "calendar", label: "Kalenteri" },
                  { key: "pricing",  label: "Hinnoittelu" },
                ].map((t) => {
                  const isActive = active === t.key;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setActive(t.key)}
                      aria-current={isActive ? "page" : undefined}
                      className={[
                        "group relative inline-flex items-center justify-center",
                        "rounded-full px-4 py-1.5 text-sm font-semibold uppercase tracking-wide whitespace-nowrap",
                        "transition duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-pink/60",
                        isActive
                          ? "text-white bg-pink shadow-neon-pink ring-1 ring-pink dark:text-white"
                          : "text-black/80 dark:text-white/90 bg-white/80 dark:bg-white/10 hover:bg-white dark:hover:bg-white/15 ring-1 ring-black/10 dark:ring-white/15 hover:shadow-neon-primary",
                        "before:absolute before:-inset-[2px] before:rounded-full before:-z-10",
                        "before:bg-[linear-gradient(135deg,var(--tw-gradient-stops))] before:from-primary before:via-pink before:to-electric",
                        isActive
                          ? "before:opacity-60 before:blur-[8px] before:animate-glow"
                          : "before:opacity-0 before:blur-[6px] hover:before:opacity-40",
                        "after:pointer-events-none after:absolute after:inset-0 after:rounded-full after:opacity-0",
                        "after:bg-shimmer after:bg-[length:700px_100%] group-hover:after:opacity-40 group-hover:after:animate-shimmer",
                        "dark:text-shadow",
                      ].join(" ")}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

                    {/* ---------- BÄNDINÄKYMÄN MURUPOLKU + TABIT ---------- */}
          {selected.type === "band" && band && (
            <div className="my-4 space-y-2">
              <Breadcrumbs
                items={[
                  { label: "Tiliasetukset", to: "/dashboard?band=user&tab=uprofile" },
                  { label: band?.name || "Esiintyjä" },
                ]}
              />

              {/* Vain bändin tabit (ei "Oma tiliä") */}
              <nav className="flex flex-wrap items-center gap-2 justify-center md:justify-start">
                {/* ... tab-napit ... */}
              </nav>
            </div>
          )}

         {/* ---------- Info copy (user & band) ---------- */}
            {copy.subtitle && (
              <div className={` ${selected.type === "user" ? "max-w-3xl" : ""}`}>
                <p className="dark:text-white/80 text-black/80 font-customFont1
                              text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm
                              leading-relaxed ">
                  {typeof copy.subtitle === "string" ? renderWithBRs(copy.subtitle) : copy.subtitle}
                </p>

                {Array.isArray(copy.tips) && copy.tips.length > 0 && (
                  <div className="mt-3 rounded-lg bg-white/70 dark:bg-black/40 ring-1 ring-black/10 dark:ring-white/10 p-3 text-sm">
                    <ul className="list-disc pl-5 space-y-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
                      {copy.tips.map((t, i) => (<li key={i}>{t}</li>))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* --- SINUN KORTIT (ESIINTYJÄT) / TYHJÄTILA --- */}
            {selected.type === "user" && (
              <div className="mt-4 mx-auto w-full max-w-5xl">
                {noBands ? (
                  // Tyhjätila: kaikki keskelle, vähän makeempi
                  <div className="min-h-[40vh] grid place-items-center">
                    <div className="text-center">
                      <div className="
                          mx-auto w-full max-w-md
                          rounded-2xl ring-1 ring-black/10 dark:ring-white/10
                          bg-white/60 dark:bg-black/40 backdrop-blur
                          p-6 shadow-lg
                        ">
                        <div className="
                            mx-auto mb-3 flex h-10 w-10 items-center justify-center
                            rounded-full text-white
                            bg-gradient-to-r from-primary to-electric
                            ring-1 ring-white/20 shadow-neon-primary
                          ">
                          +
                        </div>
                        <h3 className="font-customFont2 text-lg md:text-xl mb-2 dark:text-white/90 text-black/90">
                          Ei esiintyjäkortteja vielä
                        </h3>
                        <p className="text-sm mb-4 dark:text-white/70 text-black/70">
                          Luo ensimmäinen esiintyjäprofiili ja aloita hallinta.
                        </p>
                          <NeonButton
                            size="sm"
                            onClick={() => navigate("/setup/add")}
                          >
                            Luo esiintyjäkortti
                          </NeonButton>

                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <h3 className="w-full text-center block dark:text-white/90 text-black/90 font-customFont2 text-2xl mb-4 font-bold">
                      Sinun kortit (esiintyjät)
                    </h3>

                    {/* Kortit + Lisää-kortti */}
                    <div className="flex flex-wrap justify-center items-stretch gap-4">
                      {bands.map((b) => {
                        const id = String(b.band_id ?? b.id);
                        const name = b.name ?? b.band_name ?? `#${id}`;
                        // Vakaa valintajärjestys ja paikallinen fallback
                        const rawImg =
                          b.profile_image_override ??            // esim. juuri ladattu väliaikainen
                          b.profile_image_url ??                 // ensisijainen julkinen URL
                          b.profile_image ??                     // mahdollinen suhteellinen polku
                          b.placeholder_image_url ??             // backendin placeholder
                          b.placeholder_image ??                 // suhteellinen placeholder
                          bandPlaceholderLocal;                  // paikallinen, aina olemassa
                          // Pidä kiinni aiemmin nähdystä toimivasta kuvasta
                        let chosen = rawImg;
                        const cached = imageCacheRef.current.get(id);
                        if (!chosen && cached) {
                          chosen = cached;
                        } else if (chosen && chosen !== bandPlaceholderLocal) {
                          imageCacheRef.current.set(id, chosen);
                        }

                        const img = absolutize(chosen);

                        return (
                          <div
                            key={id}
                            className="
                              xxxsm:w-48 xxsm:w-48 s24:w-52 xsm:w-52 sm:w-56
                              md:w-56 mdd:w-60 lg:w-60 xl:w-60 2xl:w-64
                            "
                          >
                            <div className="relative group">
                              <DashMiniCard
                                title={name}
                                subtitle={b.location || ""}
                                image={img}
                                badge={!b.is_published ? "Ei julkaistu" : null}
                                onClick={() => {
                                // ⬇️ Siirry bändille viimeksi käytettyyn tabiinsa
                                  let last = "basic";
                                  try {
                                    const s = (sessionStorage.getItem(`${LAST_TAB_PREFIX}${id}`) || "").toLowerCase();
                                    if (ALLOWED_TABS.has(s)) last = s;
                                  } catch {}
                                  setSelected({ type: "band", id });
                                  setActive(last);
                                  navigate(`/dashboard?band=${encodeURIComponent(id)}&tab=${last}`);
                                }}
                                active={selected.type === "band" && String(selectedBandId) === id}
                              />
                              {String(b.role || "").toLowerCase() === "owner" && (
                                <button
                                  type="button"
                                  onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                                  onClick={(e) => { e.stopPropagation(); e.preventDefault(); openRemoveDialog(b); }}
                                  title="Poista kortti tältä tililtä"
                                  className={[
                                    "absolute top-2 right-2 z-[60] inline-flex items-center justify-center",
                                    "h-9 w-9 rounded-full text-white ring-1 ring-white/20 shadow pointer-events-auto",
                                    "bg-black/60 hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity",
                                  ].join(" ")}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                  <span className="sr-only">Poista kortti</span>
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {/* Lisää-kortti */}
                      <div className="
                        xxxsm:w-48 xxsm:w-48 s24:w-52 xsm:w-52 sm:w-56
                        md:w-56 mdd:w-60 lg:w-60 xl:w-60 2xl:w-64
                      ">
                        <AdCard
                          canAdd={!!bandLimits.can_add_band}
                          ad={{
                            ad_title: "Lisää esiintyjäkortti",
                            cta_text: "Luo uusi kortti",
                            cta_link: "/setup/add",
                          }}
                        />

                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
        </div>

      {/* --- MOBIILI: keskitetty header (nimi + tähdet) --- */}
        {selected.type === "band" && band && (
          <div className="md:hidden">
            <div className="flex flex-col items-center justify-center text-center
                            sm:flex-row sm:items-center sm:justify-between sm:text-left gap-1 sm:gap-3">
            </div>
          </div>
        )}
        {/* --- DESKTOP: iso kuva oikealla; Oma tili kuvan alla --- */}
       {selected.type === "band" && band && (
            <div className="hidden md:block justify-self-center md:justify-self-end md:sticky md:top-24 md:col-start-2">
              <div className="relative">
                <img
                  src={absolutize(
                    pendingProfile ??
                    band.profile_image_override ??
                    band.profile_image_url ??
                    band.profile_image ??
                    band.placeholder_image_url ??
                    band.placeholder_image ??
                    bandPlaceholderLocal            // 🔒 taattu kuva
                  )}
                  onError={(e) => {
                    // jos palvelin palauttaa HTML:ää tms., pakota paikallinen placeholder
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = bandPlaceholderLocal;
                  }}
                  alt={`${band.name} – profiilikuva`}
                  className="w-64 h-64 md:w-64 lg:w-72 lg:h-72 rounded-2xl object-cover ring-1 ring-black/10 dark:ring-white/10 shadow-lg bg-black/5"
                />
                {/* rating kuvan yhteyteen */}
                <RatingBadge band={band} />
              </div>
            </div>
          )}
      </div>

      {/* Virhebanneri bändilistan haulle */}
      {loadBandsError && (
        <div className="mb-4 rounded-lg border border-red-300/60 bg-red-100/70 p-3 font-customFont2 dark:text-white/80 text-black/80 text-lg font-light text-center">
          Esiintyjien haku epäonnistui. Yritä päivittää sivu. Jos jatkuu, kirjaudu ulos ja sisään.
        </div>
      )}

      {/* Tab-contentti */}
      <div className="rounded-xl ring-1 ring-black/10 dark:ring-white/10 bg-white/70 dark:bg-black/50 backdrop-blur dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
        {selected?.type === "user" ? (
          /* Näytä spinneri kun korttilista latautuu */
          loadingBands ? (
            <PageOverlayLoading label="Ladataan korttejasi…"/>
          ) : (
            <UserAccountPanel
              user={user}
              bands={bands}
              onOpenPublic={(slug) => navigate(`/esiintyja/${slug}`)}
              exposeSave={(fn) => { saveHandlerRef.current = fn; }}
              onOpenDeleteAccount={() => setConfirmDeleteAccountOpen(true)}
            />
          )
        ) : (
          <>
           {/* MOBILE ONLY (≤767px): iso profiilikuva keskelle + nimi + tähdet */}
            {selected.type === "band" && band && (
              <div className="hidden max-[767px]:block mb-4 pt-4">
                <div className="text-center">
                  <img
                    src={absolutize(
                      pendingProfile ??
                        band.profile_image_override ??
                        band.profile_image_url ??
                        band.profile_image ??
                        band.placeholder_image_url ??
                        band.placeholder_image ??
                        bandPlaceholderLocal
                    )}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = bandPlaceholderLocal;
                    }}
                    alt={`${band.name} – profiilikuva`}
                    className="mx-auto w-40 h-40 sm:w-48 sm:h-48 rounded-full object-cover ring-1 ring-black/10 dark:ring-white/10 shadow-md bg-black/5"
                  />

                  <div className="mt-2 dark:text-white/80 text-black/80 font-customFont1
                                  text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm">
                    {band.name}
                  </div>

                  {/* isompi rating-kuvakejoukko kuvan alle */}
                  <div className="mt-2 flex justify-center">
                    <RatingBadge band={band} />
                  </div>
                </div>
              </div>
            )}

            {/* Lataus bändille */}
            {loadingBand && (
              <PageOverlayLoading label="Ladataan esiintyjän tietoja…" />
            )}

            {!loadingBand && band && (
              <>
                {active === "basic" && (
                  <BasicTab
                    band={band}
                    canEdit={canEdit}
                    onSaved={(patch) => {
                      setBand((v) => ({ ...v, ...patch }));
                      setChecklistVersion((v) => v + 1);  // checklist refetch
                    }}
                    onProfileChange={(url) => setPendingProfile(url)}
                    exposeSave={(fn) => {
                      saveHandlerRef.current = fn;
                      setSaveReady(!!fn);
                    }}
                  />
                )}

                {active === "media" && (
                  <MediaTab
                    band={band}
                    onSaved={(fresh) => {
                      const b = fresh?.band || fresh;       // MediaTab voi palauttaa joko {band} tai suoraan bandin
                      if (b?.id) {
                        // Päivitä näkymässä oleva bändi
                        setBand(b);

                        // Päivitä myös korttilistassa näkyvät tiedot (esim. kuva)
                        setBands((prev) =>
                          prev.map((x) =>
                            String(x.band_id ?? x.id) === String(b.id)
                              ? { ...x, ...b }
                              : x
                          )
                        );
                      }

                      setChecklistVersion((v) => v + 1);    // checklist päivittymään
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    exposeSave={(fn) => {
                      saveHandlerRef.current = fn;
                      setSaveReady(!!fn);
                    }}
                  />
                )}

                {active === "calendar" && (
                  <CalendarTab
                    band={band}
                    holdTtlHours={HOLD_TTL_HOURS}
                       onChanged={(patch) => {
                          if (patch && typeof patch === "object") {
                            setBand((prev) => ({ ...prev, ...patch }));
                          }
                          bumpChecklist();
                        }}
                    exposeSave={(fn) => {
                      // Näytä Tallenna aina kalenterissa; jos ei ole muutoksia, klikissä tulee info-toast
                      saveHandlerRef.current = fn || (async () => {
                        toast.info("Hmm..Ei tallennettavaa", { containerId: "global" });
                      });
                      setSaveReady(true);
                    }}
                  />
                )}

                {active === "pricing" && (
                  <PricingTab
                    band={band}
                    onSaved={async () => {
                      // ⬇️ haetaan tuore bändi backendistä, jotta kaikki pallot/checklistit saa oikean datan
                      if (!band?.id) {
                        setChecklistVersion((v) => v + 1);
                        return;
                      }

                      try {
                        const data = await api.bandByIdOrSlug(band.id);
                        const fresh = data?.band || data || null;
                        if (fresh) {
                          setBand(fresh);
                        }
                      } catch (e) {
                        console.error("band reload after pricing save failed", e);
                        // ei kaadeta mitään, checklisti päivittyy silti
                      } finally {
                        setChecklistVersion((v) => v + 1);
                      }
                    }}
                    exposeSave={(fn) => {
                      saveHandlerRef.current = fn;
                      setSaveReady(!!fn);
                    }}
                  />
                )}

                {active === "social" && (
                  <SocialTab
                    band={band}
                    canEdit={canEdit}
                    onSaved={(patch) => {
                      setBand((v) => ({ ...v, ...patch }));
                      setChecklistVersion((v) => v + 1); // checklist refetch
                    }}
                    exposeSave={(fn) => {
                      saveHandlerRef.current = fn;
                      setSaveReady(!!fn);
                    }}
                  />
                )}
              </>
            )}

            {!loadingBand && !band && (
              (selected.type === "band" && (loadingBands || !selectedBandId))
                ? <PageOverlayLoading label="Ladataan esiintyjän tietoja…"/>
                : <p>Valitse bändi aloittaaksesi.</p>
            )}

          </>
        )}
      </div>

          {/* --- JULKAISUN TARKISTUS: alhaalla ennen nappiriviä --- */}
      {selected.type === "band" && band && (
        <div className="mt-6 md:mt-8">
          {/* MOBIILI: pelkät puuttuvat */}
          <div className="md:hidden">
            <PublishChecklist
              bandId={Number(band.id)}
              band={band}
              version={checklistVersion}
              isPublished={!!band.is_published}
              isPending={isPending}
              onlyMissing
            />
          </div>

          {/* DESKTOP: vasen puuttuvat, oikea läpimenneet */}
          <div className="hidden md:grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold mb-2 dark:text-white/90 text-black/90 text-sm">
                Puuttuu vielä
              </h4>
              <PublishChecklist
                bandId={Number(band.id)}
                band={band}
                version={checklistVersion}
                isPublished={!!band.is_published}
                isPending={isPending}
                onlyMissing
                showMissingLead={false}
              />
            </div>

            <div>
              <h4 className="font-semibold mb-2 dark:text-white/90 text-black/90 text-sm">
                Läpimenneet tarkistukset
              </h4>
              <PublishChecklist
                bandId={Number(band.id)}
                band={band}
                version={checklistVersion}
                isPublished={!!band.is_published}
                isPending={isPending}
                onlyDone
                collapsible
                defaultOpen={false}
                showRefresh
              />
            </div>
          </div>
        </div>
      )}

      {/* ---------- HYLKÄYSSYY (vain band + rejected) ---------- */}
        {selected.type === "band" && band && isRejected && (
          <div className="mt-2 mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            <p className="font-semibold">
              Profiili hylättiin tarkistuksessa.
            </p>
            {band.rejection_reason && (
              <p className="mt-1 whitespace-pre-line">
                {band.rejection_reason}
              </p>
            )}
            <p className="mt-2 text-xs text-red-700">
              Korjaa puutteet ja lähetä profiili uudelleen tarkistettavaksi.
            </p>
          </div>
        )}

      {/* --- STICKY ACTION BAR: vain bändille --- */}
      {selected.type === "band" && !!band && (
        <div
          className="
            sticky bottom-2 md:bottom-4 mdd:bottom-6 lg:bottom-8 xl:bottom-10 2xl:bottom-12
            z-40 mt-4
          "
        >
          <div className="mx-auto w-full max-w-7xl px-0 md:px-0">
            <div
              className="
                rounded-xl md:rounded-2xl
                shadow-lg ring-1 ring-black/10 dark:ring-white/10
                bg-white/90 dark:bg-black/70 backdrop-blur
                px-4 py-3
              "
            >
              <div
                className="
                  flex flex-col gap-3
                  md:flex-row md:gap-3 md:items-center md:justify-between md:flex-nowrap
                  mdd:flex-row mdd:gap-3 mdd:items-center mdd:justify-between mdd:flex-nowrap
                  lg:flex-row  lg:gap-3  lg:items-center  lg:justify-between  lg:flex-nowrap
                  xl:flex-row  xl:gap-3  xl:items-center  xl:justify-between  xl:flex-nowrap
                  2xl:flex-row 2xl:gap-3  2xl:items-center  2xl:justify-between  2xl:flex-nowrap
                "
              >
                <div className="flex items-center gap-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-sm">
                  <span className="opacity-70">Tila =</span>
                  {isPending ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 border border-amber-500/20">
                      <span className="font-customFont1">Odottaa hyväksyntää</span>
                      <span aria-hidden>⏳</span>
                    </span>
                  ) : isPublished ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-600/10 text-green-600 border border-green-600/20">
                      <span className="font-customFont1">Julkaistu</span>
                      <span aria-hidden>✅</span>
                    </span>
                  ) : isRejected ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600/10 text-red-500 border border-red-500/25">
                      <span className="font-customFont1">Hylätty – täydennä profiilia</span>
                      <span aria-hidden>❌</span>
                    </span>
                    ) : isApprovedHidden ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-200 border border-slate-500/25">
                      <span className="font-customFont1">Hyväksytty – ei julkaistu</span>
                      <span aria-hidden>👀</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 text-rose-500 border border-rose-500/20">
                      <span className="font-customFont2 decoration-rose-400/60">Piilotettu</span>
                      <span aria-hidden>🥷🏽</span>
                    </span>
                  )}
                </div>

                <div
                  className="
                    flex flex-col gap-2 w-full
                    md:flex-row md:gap-3 md:w-auto md:ml-auto
                    mdd:flex-row mdd:gap-3 mdd:w-auto mdd:ml-auto
                    lg:flex-row  lg:gap-3  lg:w-auto  lg:ml-auto
                    xl:flex-row  xl:gap-3  xl:w-auto  xl:ml-auto
                    2xl:flex-row 2xl:gap-3 2xl:w-auto 2xl:ml-auto
                  "
                >
                  {showSaveBtn && (
                    <NeonButton
                      type="button"
                      variant="primary"
                      size="md"
                      onClick={handleSaveClick}
                      className="w-full scale-[0.80] md:scale-100"
                      disabled={!canEdit || saving}
                    >
                      {saving ? "Tallennetaan…" : "Tallenna"}
                    </NeonButton>
                  )}

                  <NeonButton
                    type="button"
                    onClick={onTogglePublish}
                    disabled={
                      !canPublish ||
                      publishing ||
                      submittingReview ||
                      isPending ||
                      reviewLoading || (!isPublished && !reviewReady)
                    }
                    title={
                      isPending
                        ? "Odottaa hyväksyntää"
                        : reviewLoading
                        ? "Tarkistetaan vaatimukset…"
                        : (!isPublished && !reviewReady)
                        ? "Täydennä puuttuvat kohdat ennen lähetystä"
                        : undefined
                    }
                    className="w-full scale-[0.80] md:scale-100"
                  >
                  {submittingReview
                    ? "Lähetetään…"
                    : publishing
                      ? "Päivitetään…"
                      : isPending
                        ? "Odottaa hyväksyntää"
                        : isPublished
                          ? "Piilota profiili"
                          : isRejected
                            ? (reviewReady
                                ? "Lähetä uudelleen tarkistukseen"
                                : "Täydennä ja lähetä uudelleen")
                            : (reviewReady ? "Lähetä tarkistukseen" : "Julkaise profiili")}

                  </NeonButton>

                  <NeonButton
                    type="button"
                      onClick={() => {
                      // ⬇️ Muista paluukohde (bändi + tab) ennen esikatselua
                        try {
                          if (band?.id) {
                            sessionStorage.setItem(PREVIEW_RETURN_BAND_KEY, String(band.id));
                            sessionStorage.setItem(PREVIEW_RETURN_TAB_KEY, String(active).toLowerCase());
                            sessionStorage.setItem(`${LAST_TAB_PREFIX}${band.id}`, String(active).toLowerCase());
                          }
                        } catch {}

                        const t =
                          sessionStorage.getItem("bbx.previewToken") ||
                          localStorage.getItem("bbx.previewToken") ||
                          band?.review_token ||
                          band?.preview_token ||
                          "";
                        const qs = new URLSearchParams();
                        qs.set("preview", "1");            // ✅ tämä kytkee esikatselutilan
                        if (t) qs.set("previewToken", t);  // ✅ sallii esikatselun myös ei-julkaistulle
                        try { sessionStorage.setItem(PREVIEW_TOAST_KEY, "1"); } catch {}
                        navigate(`/esiintyja/${band.slug}?${qs.toString()}`, {
                          state: { fromDashboard: true }   // ✅ tämä näyttää esikatselupalkin alareunassa
                        });
                      }}
                    disabled={!band?.slug}
                    className="w-full scale-[0.80] md:scale-100"
                  >
                    Esikatsele profiilia
                  </NeonButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAALIT --- */}

      {/* Julkaisun/piilotuksen varmistukset – vain bändinäkymässä */}
      {selected.type === "band" && band && (
        <>
          {/* Julkaisun varmistus */}
          <ConfirmDialog
            open={confirmPublishOpen}
            title="Julkaistaanko profiili?"
            description="Profiilisi lähetetään ylläpidon tarkistettavaksi. Jos profiili hyväksytään, se julkaistaan ja alkaa näkyä haussa. Saat päätöksestä sähköpostin. Huom! Jos profiilissa on loukkaavaa tai muutoin asiatonta sisältöä, profiili ja tili voidaan poistaa käytöstä."
            confirmText="Lähetä tarkistettavaksi"
            cancelText="Peru"
            busy={submittingReview}
            busyText="Lähetetään…"
            onConfirm={submitForReview}
            onClose={() => setConfirmPublishOpen(false)}
          />

          {/* Piilotuksen varmistus */}
          <ConfirmDialog
            open={confirmUnpublishOpen}
            title="Piilotetaanko profiili?"
            description="Profiilisi poistuu hausta ja julkisesta näkymästä heti. Kun julkaiset sen uudelleen, profiili lähetetään taas ylläpidon tarkistettavaksi ja se näkyy haussa vasta hyväksynnän jälkeen."
            confirmText="Piilota profiili"
            cancelText="Peru"
            busy={publishing}
            busyText="Piilotetaan…"
            onConfirm={doUnpublish}
            onClose={() => setConfirmUnpublishOpen(false)}
          />
        </>
      )}

      {/* Poiston varmistus: näytetään riippumatta näkymästä (user- tai band-näkymä) */}
      <ConfirmDialog
        open={confirmRemoveOpen}
        title="Poistetaanko kortti tililtä?"
        description={
          (bandToRemove?.name
            ? `Esiintyjäkortti ${bandToRemove.name} poistetaan tältä tililtä.`
            : "Kortti poistetaan tältä tililtä.") +
          "\n\nHuom: Korttia/esiintyjää ei voi poistaa, jos kalenterissa on vahvistettuja esiintymisiä tai aktiivisia tarjouspyyntöjä!"
        }
        confirmText="Poista kortti"
        cancelText="Peru"
        busy={removing}
        busyText="Poistetaan…"
        onConfirm={doRemoveBand}
        onClose={() => setConfirmRemoveOpen(false)}
      />

      <ConfirmDialog
        open={confirmDeleteAccountOpen}
        title="Poistetaanko tili pysyvästi?"
        description={
          "Tämä poistaa käyttäjätilisi ja siihen liitetyt tiedot pysyvästi. " +
          "Toimintoa ei voi perua. Jos tilillä on esiintyjäkortteja, ne poistetaan tai " +
          "niiden hallinta voi lakata toimimasta. Haluatko jatkaa?"
        }
        confirmText="Kyllä, poista tili"
        cancelText="Peru"
        busy={deletingAccount}
        busyText="Poistetaan…"
        onConfirm={deleteAccount}
        onClose={() => setConfirmDeleteAccountOpen(false)}
      />


       {/* ⬇️ UUSI: viimeinen omistaja – tarjoa koko esiintyjän poisto */}
      <ConfirmDialog
        open={confirmDeleteBandOpen}
        title="Poistetaanko esiintyjä kokonaan?"
        description={
          (bandToRemove?.name
            ? `“${bandToRemove.name}” poistetaan pysyvästi.`
            : "Esiintyjä poistetaan pysyvästi.") +
          "\n\nTämä poistaa profiilin, kuvat, hinnat ym. Poisto onnistuu vain, jos kalenterissa ei ole vahvistettuja esiintymisiä tai aktiivisia pyyntöjä."
        }
        confirmText="Poista esiintyjä"
        cancelText="Peru"
        busy={deletingBand}
        busyText="Poistetaan…"
        onConfirm={doDeleteBand}
        onClose={() => setConfirmDeleteBandOpen(false)}
      />

       {/* 🌐 Web-sivumainos: näkyy vain kirjautuneelle käyttäjälle */}
      {ENABLE_WEBDEV_PROMO && (
        <WebDevPromoModal
          open={showWebDevPromo}
          onClose={() => setShowWebDevPromo(false)}
          onNeverShow={() => {
            try {
              localStorage.setItem("webdev_promo_dismissed", "1");
            } catch {}
            setShowWebDevPromo(false);
          }}
        />
      )}

      
      
    </div>
  );
}
